'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Globe, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Review {
  id: string;
  guest: string;
  property: string;
  rating: number;
  text: string;
  date: string;
}

const propertyLabels: Record<string, string> = {
  skycabin: 'Skycabin',
  levikka: 'Levikka',
  varakka: 'Varakka',
};

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'fi', label: 'Suomi', flag: '🇫🇮' },
  { code: 'sv', label: 'Svenska', flag: '🇸🇪' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
  { code: 'af', label: 'Afrikaans', flag: '🇿🇦' },
  { code: 'divider', label: '', flag: '' },
  { code: 'redneck', label: 'Redneck', flag: '🤠' },
{ code: 'aussie', label: 'Aussie', flag: '🦘' },
  { code: 'canadian-french', label: 'Québécois', flag: '🍁' },
  { code: 'scottish', label: 'Scottish', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿' },
  { code: 'irish', label: 'Irish', flag: '🍀' },
];

function StarRating() {
  return (
    <div className="flex gap-0.5 justify-center">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="w-4 h-4 text-[#C4A95E] fill-[#C4A95E]" />
      ))}
    </div>
  );
}

export default function ReviewCarousel({
  reviews,
  eyebrow = 'Guest Reviews',
  title = 'What Our Guests Say',
  translatingLabel = 'Translating...',
  translatedLabel = 'Translated from original',
  funDividerLabel = 'Just for fun',
  prevLabel = 'Previous review',
  nextLabel = 'Next review',
  gotoTemplate = 'Go to review {n}',
}: {
  reviews: Review[];
  eyebrow?: string;
  title?: string;
  translatingLabel?: string;
  translatedLabel?: string;
  funDividerLabel?: string;
  prevLabel?: string;
  nextLabel?: string;
  gotoTemplate?: string;
}) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lang, setLang] = useState('en');
  const [langOpen, setLangOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fiveStarReviews = reviews.filter((r) => r.rating === 5);

  const next = useCallback(() => {
    if (fiveStarReviews.length === 0) return;
    setCurrent((c) => (c + 1) % fiveStarReviews.length);
  }, [fiveStarReviews.length]);

  const prev = useCallback(() => {
    if (fiveStarReviews.length === 0) return;
    setCurrent((c) => (c - 1 + fiveStarReviews.length) % fiveStarReviews.length);
  }, [fiveStarReviews.length]);

  useEffect(() => {
    if (paused || fiveStarReviews.length <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [paused, next, fiveStarReviews.length]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Translate current review when language changes
  useEffect(() => {
    if (lang === 'en' || fiveStarReviews.length === 0) return;

    const review = fiveStarReviews[current];
    const cacheKey = `${review.id}:${lang}`;

    if (translations[review.id]?.[lang]) return;

    let cancelled = false;
    setTranslating(true);

    fetch(`/api/translate?target=${lang}&text=${encodeURIComponent(review.text)}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.translated) {
          setTranslations((prev) => ({
            ...prev,
            [review.id]: { ...prev[review.id], [lang]: data.translated },
          }));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTranslating(false);
      });

    return () => { cancelled = true; };
  }, [lang, current, fiveStarReviews, translations]);

  if (fiveStarReviews.length === 0) return null;

  const review = fiveStarReviews[current];
  const displayText =
    lang === 'en' ? review.text : translations[review.id]?.[lang] || review.text;
  const isTranslated = lang !== 'en' && translations[review.id]?.[lang];

  const currentLang = LANGUAGES.find((l) => l.code === lang);

  return (
    <section
      className="py-20 px-6 bg-[#F0EDE5]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div className="flex-1">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.25em] mb-3 text-[#5A5A6E]">
              {eyebrow}
            </p>
            <h2 className="font-sans text-3xl md:text-4xl font-light text-[#1A1A2E]">
              {title}
            </h2>
          </div>

          {/* Language Selector */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/80 hover:bg-white shadow-sm text-sm transition-colors cursor-pointer"
            >
              <Globe className="w-4 h-4 text-[#9B7B3A]" />
              <span>{currentLang?.flag}</span>
              <span className="hidden sm:inline text-[#1A1A2E]">{currentLang?.label}</span>
            </button>

            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 w-48 max-h-80 overflow-y-auto"
                >
                  {LANGUAGES.map((l) => {
                    if (l.code === 'divider') {
                      return (
                        <div key="divider" className="my-1 border-t border-gray-100 mx-3">
                          <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-2 mb-1 px-1">
                            {funDividerLabel}
                          </p>
                        </div>
                      );
                    }
                    const isActive = lang === l.code;
                    return (
                      <button
                        key={l.code}
                        onClick={() => {
                          setLang(l.code);
                          setLangOpen(false);
                        }}
                        className={cn(
                          'w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors cursor-pointer',
                          isActive
                            ? 'bg-[#F5F0E8] text-[#9B7B3A] font-medium'
                            : 'text-[#1A1A2E] hover:bg-gray-50'
                        )}
                      >
                        <span className="text-base">{l.flag}</span>
                        <span>{l.label}</span>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="relative">
          {/* Nav buttons */}
          {fiveStarReviews.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-md flex items-center justify-center transition-colors cursor-pointer z-10"
                aria-label={prevLabel}
              >
                <ChevronLeft className="w-5 h-5 text-[#1A1A2E]" />
              </button>
              <button
                onClick={next}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-md flex items-center justify-center transition-colors cursor-pointer z-10"
                aria-label={nextLabel}
              >
                <ChevronRight className="w-5 h-5 text-[#1A1A2E]" />
              </button>
            </>
          )}

          {/* Review card */}
          <div className="overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${review.id}-${lang}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
                className="bg-white rounded-2xl p-8 md:p-12 shadow-lg shadow-slate-200/40 text-center"
              >
                <StarRating />
                <div className="relative mt-6 mb-6 min-h-[80px]">
                  {translating && !isTranslated ? (
                    <div className="flex items-center justify-center gap-2 text-[#5A5A6E]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{translatingLabel}</span>
                    </div>
                  ) : (
                    <p className="font-sans text-lg md:text-xl leading-relaxed text-[#1A1A2E] max-w-2xl mx-auto">
                      &ldquo;{displayText}&rdquo;
                    </p>
                  )}
                </div>
                <p className="font-semibold text-[#1A1A2E]">{review.guest}</p>
                <p className="text-sm text-[#9B7B3A] mt-1">
                  {propertyLabels[review.property] || review.property}
                </p>
                {isTranslated && (
                  <p className="text-xs text-[#5A5A6E]/60 mt-3">
                    {translatedLabel}
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          {fiveStarReviews.length > 1 && (
            <div className="flex flex-wrap justify-center gap-0 mt-6">
              {fiveStarReviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`flex h-11 w-11 items-center justify-center rounded-full cursor-pointer after:h-2 after:rounded-full after:transition-all ${
                    i === current ? 'after:bg-[#9B7B3A] after:w-6' : 'after:bg-[#9B7B3A]/30 after:w-2'
                  }`}
                  aria-pressed={i === current}
                  aria-label={gotoTemplate.replace('{n}', String(i + 1))}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
